// src/view/app/DicomViewerNew/utils/multiView_simple.js
import RadiologistApi from "../../../../services/ApiServices/RadiologistApi";
import axios from "axios";
import JSZip from "jszip";

// --- debug helpers ---
const DBG = true;
const log = (...a) => DBG && console.log("[MV]", ...a);
const errInfo = (e) => ({
  msg: e?.message,
  url: e?.config?.url,
  status: e?.response?.status,
  data: e?.response?.data,
});

/** Use a fresh axios instance for S3 (bypasses app-wide interceptors) */
function makeS3() {
  const s3 = axios.create();
  s3.interceptors.response.use(
    (r) => r,
    (e) => {
      log("S3:error", errInfo(e));
      return Promise.reject(e);
    }
  );
  return s3;
}

/** same unzip as your single flow, but reused here */
async function unzipDicomZip(zipUrl, s3 = makeS3()) {
  log("unzip:start", { zipUrl });
  const zipBuf = await s3.get(zipUrl, { responseType: "arraybuffer" });
  log("unzip:downloaded", {
    bytes:
      typeof zipBuf?.data?.byteLength === "number"
        ? zipBuf.data.byteLength
        : undefined,
  });

  const zip = await JSZip.loadAsync(zipBuf.data);
  const entries = Object.values(zip.files).filter((f) => !f.dir);
  log("unzip:entries", { total: entries.length });

  // keep original names like your single flow
  const files = await Promise.all(
    entries.map(async (f) => new File([await f.async("blob")], f.name))
  );

  if (!files.length) throw new Error("No DICOM files found in ZIP");
  log("unzip:done", { count: files.length });
  return files;
}

/** hydrate ONE exam exactly like handleOpenDICOMViewer, carry createdAt */
export async function hydrateExam(examId, patientId, extra = {}) {
  const { createdAt } = extra || {};
  log("hydrateExam:start", { examId, patientId, createdAt });

  let dicomUrl, resultUrl;
  try {
    ({ dicomUrl, resultUrl } = await RadiologistApi.getResults(examId));
    log("getResults:ok", {
      examId,
      haveDicom: !!dicomUrl,
      haveJson: !!resultUrl,
    });
  } catch (e) {
    log("getResults:err", errInfo(e));
    throw e;
  }

  if (!dicomUrl || !resultUrl) {
    throw new Error(`Missing URLs for examId=${examId}`);
  }

  const s3 = makeS3();

  const [dicomFiles, resultResp] = await Promise.all([
    unzipDicomZip(dicomUrl, s3).catch((e) => {
      log("unzip:err", { examId, ...errInfo(e) });
      throw e;
    }),
    s3.get(resultUrl).catch((e) => {
      log("json:err", { examId, ...errInfo(e), resultUrl });
      throw e;
    }),
  ]);

  const out = {
    patientId,
    accessionNumber: examId,
    caseId: examId,
    createdAt,
    dicomFiles,
    jsonData: resultResp.data,
  };

  log("hydrateExam:done", {
    examId,
    slices: dicomFiles.length,
    hasJson: !!out.jsonData,
    createdAt: out.createdAt,
  });
  return out;
}

/** load up to `limit` other exams in parallel and hydrate them (keep createdAt) */
export async function loadMultiExams(patientId, currentExamId, limit = 3) {
  log("loadMultiExams:start", { patientId, currentExamId, limit });

  let exams = [];
  try {
    const data = await RadiologistApi.getAllCasesByPatient(patientId);
    exams = data?.exams || [];
    log("getAllCasesByPatient:ok", { count: exams.length });
  } catch (e) {
    log("getAllCasesByPatient:err", errInfo(e));
    throw e;
  }

  // Choose latest COMPLETE exams (exclude current), keep createdAt per exam
  const others = exams
    .filter((e) => {
      const id = String(e.examId ?? e.caseId ?? e.accessionNumber ?? "");
      const status = String(e.status ?? "").toLowerCase();
      return (
        id &&
        id !== String(currentExamId) &&
        (status === "complete" || status === "completed")
      );
    })
    .sort((a, b) => {
      const ta = new Date(a.createdAt ?? 0).getTime();
      const tb = new Date(b.createdAt ?? 0).getTime();
      return tb - ta; // newest first
    })
    .slice(0, limit)
    .map((e) => ({
      id: String(e.examId ?? e.caseId ?? e.accessionNumber),
      createdAt: e.createdAt,
    }));

  log("loadMultiExams:ids", others);

  const settled = await Promise.allSettled(
    others.map((o) => hydrateExam(o.id, patientId, { createdAt: o.createdAt }))
  );

  settled.forEach((r, i) => {
    const { id } = others[i];
    if (r.status === "fulfilled") {
      log("hydrateExam:fulfilled", {
        examId: id,
        slices: r.value?.dicomFiles?.length,
        hasJson: !!r.value?.jsonData,
        createdAt: r.value?.createdAt,
      });
    } else {
      log("hydrateExam:rejected", { examId: id, reason: r.reason?.message });
    }
  });

  const ok = settled
    .filter((r) => r.status === "fulfilled")
    .map((r) => r.value);

  log("loadMultiExams:done", {
    ok: ok.length,
    fail: others.length - ok.length,
  });
  return ok;
}
